// /app/frontend/src/rules/propertyFieldRules.js
// Single source of truth for conditional fields visibility + safe resets.
// Taxonomy expected shape:
// { operation, objet, profil, sous_profil, cible }

import coreFieldsRules from './coreFieldsRules.json';

// Export coreFieldsRules for component access to field metadata
export { coreFieldsRules };

// ---------------------------
// CORE Field Mapping: JSON ID -> actual formData path
// ---------------------------
// Maps field IDs from coreFieldsRules.json to actual paths in formData.
// This allows us to use clean semantic IDs in JSON while maintaining existing formData structure.
const CORE_FIELD_MAPPING = {
  // Taxonomy (already aligned)
  'taxonomy.operation': 'operation',
  'taxonomy.objet': 'objet',
  'taxonomy.profil': 'profil',
  'taxonomy.sous_profil': 'sous_profil',
  'taxonomy.cible': 'cible',
  
  // Public location
  'public_location.sector_label': 'bien_general.secteur',
  'public_location.emplacement_code': 'bien_general.emplacement',
  'public_location.emplacement_note': 'bien_general.emplacement_note',
  
  // Surfaces
  'composition.surfaces.surface_totale_m2': 'composition.superficie_totale',
  'composition.surfaces.surface_salle_m2': 'composition.surface_salle',
  'composition.surfaces.surface_cuisine_m2': 'composition.surface_cuisine',
  'composition.surfaces.surface_reserve_m2': 'composition.reserve',
  'composition.surfaces.surface_terrasse_m2': 'composition.terrasse_m2',
  'composition.surfaces.surface_vente_m2': 'composition.surface_vente',
  'composition.surfaces.surface_mezzanine_m2': 'composition.surface_mezzanine',
  'composition.surfaces.surface_bureau_m2': 'composition.bureaux_m2',
  'composition.surfaces.surface_open_space_m2': 'composition.surface_open_space',
  'composition.surfaces.surface_bureaux_fermes_m2': 'composition.surface_bureaux_fermes',
  'composition.surfaces.surface_salles_reunion_m2': 'composition.surface_salles_reunion',
  'composition.surfaces.surface_accueil_m2': 'composition.surface_accueil',
  'composition.surfaces.surface_archives_m2': 'composition.surface_archives',
  'composition.surfaces.surface_kitchenette_m2': 'composition.surface_kitchenette',
  'composition.surfaces.surface_stockage_m2': 'composition.surface_stockage',
  
  // Capacités
  'composition.capacites.couverts_interieur': 'composition.places_interieur',
  'composition.capacites.couverts_terrasse': 'composition.places_terrasse',
  'composition.capacites.couverts_total': 'composition.nombre_couverts_total',
  'composition.capacites.places_assises_interieur': 'composition.places_interieur',
  'composition.capacites.places_assises_terrasse': 'composition.places_terrasse',
  'composition.capacites.nombre_salles': 'composition.nombre_salles',
  'composition.capacites.salle_privee': 'composition.salle_privee',
  'composition.capacites.postes_travail': 'composition.postes_travail',
  'composition.capacites.salles_reunion_nombre': 'composition.salles_reunion_nombre',
  
  // Équipements
  'composition.equipements.climatisation': 'composition.climatisation',
  'composition.equipements.climatisation_type': 'composition.climatisation_type',
  'composition.equipements.fibre_optique': 'composition.fibre_optique',
  'composition.accessibilite.pmr_accessible': 'bien_general.accessibilite_pmr',
  
  // CHR specifics
  'composition.chr.terrasse_existe': 'bien_general.terrasse',
  'composition.chr.terrasse_type': 'composition.terrasse_type',
  'composition.chr.terrasse_couverte': 'composition.terrasse_couverte',
  'composition.chr.terrasse_chauffee': 'composition.terrasse_chauffee',
  'composition.chr.autorisation_terrasse': 'composition.autorisation_terrasse',
  'composition.chr.redevance_terrasse_an': 'composition.redevance_terrasse_an',
  'composition.chr.cuisine_type': 'composition.type_cuisine',
  'composition.chr.extraction_existe': 'composition.extraction_existe',
  'composition.chr.extraction_diametre_mm': 'composition.extraction_diametre_mm',
  'composition.chr.extraction_toiture': 'composition.extraction_toiture',
  'composition.chr.gaine_extraction': 'composition.gaine_extraction',
  'composition.chr.electricite_tri': 'composition.electricite_tri',
  'composition.chr.gaz_existe': 'composition.gaz_existe',
  'composition.chr.puissance_kw': 'composition.puissance_kw',
  'composition.chr.chambre_froide': 'composition.chambre_froide',
  'composition.chr.nombre_chambres_froides': 'composition.nombre_chambres_froides',
  'composition.chr.bar_existe': 'composition.bar_existe',
  'composition.chr.tireuse_biere': 'composition.tireuse_biere',
  
  // Retail specifics
  'composition.retail.lineaire_vitrine_m': 'composition.lineaire_facade',
  'composition.retail.angle_rue': 'composition.angle_rue',
  'composition.retail.double_vitrine': 'composition.double_vitrine',
  'composition.retail.hauteur_sous_plafond_m': 'composition.hauteur_plafond',
  'composition.retail.rideau_metal': 'composition.rideau_metal',
  'composition.retail.alarme': 'composition.alarme',
  'composition.retail.reserve_securisee': 'composition.reserve_securisee',
  'composition.retail.flux_pieton': 'bien_general.flux_pieton',
  'composition.retail.stationnement_proximite': 'bien_general.parking_proximite',
  'composition.retail.proximite_transports': 'bien_general.transports_proximite',
  
  // Bureaux specifics
  'composition.bureaux.divisible': 'composition.divisible',
  'composition.bureaux.surface_min_divisible_m2': 'composition.surface_min_divisible',
  'composition.bureaux.phone_booths': 'composition.phone_booths',
  'composition.bureaux.cablage_rj45': 'composition.cablage_rj45',
  'composition.bureaux.baie_brassage': 'composition.baie_brassage',
  'composition.bureaux.faux_plancher': 'composition.faux_plancher',
  'composition.bureaux.faux_plafond': 'composition.faux_plafond',
  'composition.bureaux.chauffage_type': 'composition.chauffage_type',
  
  // Accès
  'composition.acces.ascenseur': 'composition.ascenseur',
  'composition.acces.nombre_niveaux': 'composition.nombre_niveaux',
  'composition.acces.controle_acces': 'composition.controle_acces',
  'composition.acces.erp': 'bien_general.classement_erp',
  'composition.acces.erp_categorie': 'composition.erp_categorie',
  'composition.acces.parking_places': 'composition.parking_places',

  // Hôtellerie (spécifique aux hôtels)
  'composition.hotel.prix_moyen_nuitee': 'composition.prix_moyen_nuitee',
  'composition.hotel.type_reception': 'composition.type_reception',
  'composition.hotel.horaires_reception': 'composition.horaires_reception',

  // Bar & restauration — gating restaurants
  'composition.chr.nombre_restaurants': 'composition.nombre_restaurants',
  
  // Activité/Logistique
  'composition.activite.hauteur_libre_m': 'composition.hauteur_libre',
  'composition.activite.quais_chargement_nombre': 'composition.quais_chargement',
  'composition.activite.portes_sectionnelles_nombre': 'composition.portes_sectionnelles',
  'composition.activite.plain_pied': 'composition.plain_pied',
  'composition.activite.aire_manoeuvre': 'composition.aire_manoeuvre',
  'composition.activite.charge_sol_kg_m2': 'composition.charge_sol',
  'composition.activite.pont_roulant': 'composition.pont_roulant',
  'composition.activite.capacite_pont_t': 'composition.capacite_pont',
  'composition.activite.electricite_tri': 'composition.electricite_tri',
  'composition.activite.sprinkler': 'composition.sprinkler',
  'composition.activite.icpe': 'composition.icpe',
  'composition.activite.frigorifique': 'composition.frigorifique',
  'composition.activite.temperature_dirigee': 'composition.temperature_dirigee',
  'composition.activite.puissance_kw': 'composition.puissance_kw',
  'composition.activite.extraction': 'composition.extraction_existe',
  'composition.activite.hauteur_plafond_m': 'composition.hauteur_plafond',
  'composition.activite.acces_livraison': 'composition.acces_livraison',
  
  // Licence
  'licence.licence_type': 'bien_general.type_licence',
  'licence.licence_categorie': 'bien_general.type_licence',
  'licence.commune_rattachement': 'bien_general.commune_rattachement',
  'licence.departement': 'bien_general.departement',
  'licence.date_derniere_exploitation': 'bien_general.date_derniere_exploitation',
  'licence.transferable': 'bien_general.transferable',
  'licence.conditions_transfert': 'bien_general.conditions_transfert',
  'licence.valeur_estimee': 'informations_vente.valeur_estimee',
  'licence.prix_net_vendeur': 'honoraires_calcul.net_vendeur',
  
  // Investissement/Murs
  'investissement.copropriete': 'bien_general.copropriete',
  'investissement.charges_annuelles': 'informations_vente.charges_annuelles',
  'investissement.taxe_fonciere': 'informations_bail.impot_foncier',
  'investissement.occupe': 'bien_general.occupe',
  'investissement.loyer_annuel_actuel': 'informations_bail.loyer_annuel',
  'investissement.bail_type': 'informations_bail.titre_locatif',
  'investissement.echeance_bail': 'informations_bail.echeance_bail',
  'investissement.taux_rendement_brut': 'informations_vente.taux_rendement_brut',
  'investissement.taux_rendement_net': 'informations_vente.taux_rendement_net',
  
  // Services & Professions libérales
  'composition.services.nb_pieces': 'composition.nb_pieces',
  'composition.services.nb_bureaux': 'composition.nb_bureaux',
  'composition.services.surface_bureaux_m2': 'composition.surface_bureaux',
  'composition.services.salle_attente': 'composition.salle_attente',
  'composition.services.surface_salle_attente_m2': 'composition.surface_salle_attente',
  'composition.services.secretariat_accueil': 'composition.secretariat_accueil',
  'composition.services.surface_secretariat_m2': 'composition.surface_secretariat',
  'composition.services.nb_sanitaires': 'composition.nb_sanitaires',
  'composition.services.nb_salles_eau': 'composition.nb_salles_eau',
  'composition.services.kitchenette': 'composition.kitchenette',
  'composition.services.surface_kitchenette_m2': 'composition.surface_kitchenette',
  'composition.services.archives': 'composition.archives',
  'composition.services.surface_archives_m2': 'composition.surface_archives',
  'composition.services.local_technique': 'composition.local_technique',
  'composition.services.parking_places': 'composition.parking_places',
  
  // Medical specifics
  'composition.medical.nb_salles_consultation': 'composition.nb_salles_consultation',
  'composition.medical.nb_salles_soins': 'composition.nb_salles_soins',
  'composition.medical.nb_fauteuils_dentaires': 'composition.nb_fauteuils_dentaires',
  'composition.medical.salle_sterilisation': 'composition.salle_sterilisation',
  'composition.medical.radio_panoramique': 'composition.radio_panoramique',
  'composition.medical.salle_prelevement': 'composition.salle_prelevement',
  'composition.medical.stockage_refrigere': 'composition.stockage_refrigere',
  'composition.medical.vestiaires': 'composition.vestiaires',
  'composition.medical.normes_medicales': 'composition.normes_medicales',
  'composition.medical.pmr_obligatoire': 'composition.pmr_obligatoire',
  
  // Juridique specifics
  'composition.juridique.nb_salles_reunion': 'composition.nb_salles_reunion',
  'composition.juridique.salle_signature': 'composition.salle_signature',
  'composition.juridique.archives_securisees': 'composition.archives_securisees',
  'composition.juridique.coffre_fort': 'composition.coffre_fort',
  'composition.juridique.insonorisation': 'composition.insonorisation',
  
  // Commerce services specifics
  'composition.commerce_services.vitrine': 'composition.vitrine',
  'composition.commerce_services.lineaire_vitrine_m': 'composition.lineaire_vitrine',
  'composition.commerce_services.espace_accueil_client': 'composition.espace_accueil_client',
  'composition.commerce_services.nb_postes_accueil': 'composition.nb_postes_accueil',
};

/**
 * Resolve a field ID from coreFieldsRules.json to actual formData path.
 * If no mapping exists, returns the field ID as-is (for future-proofing).
 */
export function resolveFieldPath(fieldId) {
  return CORE_FIELD_MAPPING[fieldId] || fieldId;
}

/**
 * Helper to get value from nested object by dot-path.
 * Safe accessor: returns undefined if path doesn't exist.
 * Exported for use in components.
 */
export function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

/**
 * Evaluate a 'when' condition from coreFieldsRules.json.
 * Supports:
 *   - Simple equality: { "path": true }
 *   - Operator form : { "path": { "gt": 0 } | { "gte": 0 } | { "lt": N } | { "lte": N } | { "neq": X } | { "in": [...] } }
 *
 * @param {Object} formData - Current form data
 * @param {Object} whenObj  - Condition object from JSON
 * @returns {boolean}
 */
export function isWhenSatisfied(formData, whenObj) {
  if (!whenObj || typeof whenObj !== 'object') return true;

  for (const [fieldId, expected] of Object.entries(whenObj)) {
    const actualPath = resolveFieldPath(fieldId);
    const actualValue = getByPath(formData, actualPath);

    // Operator form: { gt/gte/lt/lte/neq/in: X }
    if (expected && typeof expected === 'object' && !Array.isArray(expected)) {
      const asNum = actualValue == null || actualValue === '' ? null : Number(actualValue);
      if ('gt' in expected)  { if (!(asNum != null && asNum > Number(expected.gt)))  return false; }
      if ('gte' in expected) { if (!(asNum != null && asNum >= Number(expected.gte))) return false; }
      if ('lt' in expected)  { if (!(asNum != null && asNum < Number(expected.lt)))  return false; }
      if ('lte' in expected) { if (!(asNum != null && asNum <= Number(expected.lte))) return false; }
      if ('neq' in expected) { if (actualValue === expected.neq) return false; }
      if ('in' in expected)  { if (!Array.isArray(expected.in) || !expected.in.includes(actualValue)) return false; }
      continue;
    }

    // Simple equality (works for booleans, strings, numbers)
    if (actualValue !== expected) return false;
  }

  return true;
}

const TAX = {
  operation: ["cession", "vente_immo", "location", "location_gerance"],
  objet: [
    "fonds_de_commerce",
    "droit_au_bail",
    "licence",
    "parts_sociales",
    "local_commercial",
    "bureaux",
    "local_activite",
    "entrepot_logistique",
    "immeuble",
    "terrain",
    "murs_commerciaux",
  ],
  profil: ["chr", "retail", "tertiaire", "industriel", "logistique", "investissement"],
};

// ---------------------------
// Field IDs (stable keys)
// ---------------------------
// IMPORTANT: these IDs must match your formData paths.
// If your formData uses nested objects (recommended), keep these dot-paths consistent.
export const FIELDS = {
  // --- Taxonomy
  operation: "operation",
  objet: "objet",
  profil: "profil",
  sous_profil: "sous_profil",
  cible: "cible",

  // --- Essential / Public location
  secteur: "bien_general.secteur",
  ville_generale: "bien_general.ville_generale",
  code_postal: "bien_general.code_postal",
  zone_attractivite: "bien_general.zone_attractivite",
  emplacement: "bien_general.emplacement",
  geoloc_lat: "bien_general.geolocalisation_lat",
  geoloc_lon: "bien_general.geolocalisation_lon",

  // --- Status / mandate / negotiator
  status: "status",
  numero_mandat: "numero_mandat",
  date_mandat: "bien_general.date_mandat",
  type_mandat: "bien_general.type_mandat",
  negociateur: "negociateur.negociateur",

  // --- Offer (basic)
  surface_totale: "composition.superficie_totale",
  prix_annonce: "informations_vente.prix_annonce",
  prix_minimum_accepte: "informations_vente.prix_minimum_accepte",
  loyer_mensuel: "informations_bail.loyer_mensuel",
  charges_mensuelles: "informations_bail.charges_mensuelles",  // iter130 : nom retenu (`charges` = legacy)
  droit_entree: "informations_bail.droit_entree",
  type_prix: "honoraires_calcul.type_prix",

  // --- Fees / pricing
  prix_vente: "honoraires_calcul.prix_vente",
  net_vendeur: "honoraires_calcul.net_vendeur",
  honoraires: "honoraires_calcul.honoraires",
  honoraires_type: "honoraires_calcul.honoraires_type",
  prix_minimum: "honoraires_calcul.prix_minimum",

  // --- Bail core
  titre_locatif: "informations_bail.titre_locatif",
  loyer_soumis_tva: "informations_bail.loyer_soumis_tva",
  date_debut_bail: "informations_bail.date_debut_bail",
  date_echeance_bail: "informations_bail.date_echeance_bail",
  loyer_annuel: "informations_bail.loyer_annuel",
  impot_foncier: "informations_bail.impot_foncier",
  revision_loyer: "informations_bail.revision_loyer",
  procedure: "informations_confidentielles.procedure",

  // --- Gérance
  redevance_mensuelle: "informations_bail.redevance_mensuelle",
  duree_gerance: "informations_bail.duree_gerance",
  caution_gerance: "informations_bail.caution_gerance",
  conditions_gerance: "informations_bail.conditions_particulieres_gerance",

  // --- Surfaces detailed
  surface_vente: "composition.surface_vente",
  reserve: "composition.reserve",
  surface_cuisine: "composition.surface_cuisine",
  surface_bureaux: "composition.surface_bureaux",
  surface_salles_reunion: "composition.surface_salles_reunion",
  surface_accueil: "composition.surface_accueil",
  surface_archives: "composition.surface_archives",
  surface_stockage: "composition.surface_stockage",
  surface_production: "composition.surface_production",
  surface_exterieure: "composition.surface_exterieure",
  terrasse_m2: "composition.terrasse_m2",
  bureaux_m2: "composition.bureaux_m2",
  hauteur_plafond: "composition.hauteur_plafond",
  hauteur_libre: "composition.hauteur_libre",
  lineaire_facade: "composition.lineaire_facade",

  // --- Capacities
  nombre_couverts_total: "composition.nombre_couverts_total",
  places_interieur: "composition.places_interieur",
  places_terrasse: "composition.places_terrasse",
  postes_travail: "composition.postes_travail",
  salles_reunion_nombre: "composition.salles_reunion_nombre",
  parking_places: "composition.parking_places",

  // --- Technical equipment
  fibre_optique: "composition.fibre_optique",
  climatisation: "composition.climatisation",
  accessibilite_pmr: "bien_general.accessibilite_pmr",
  parking_type: "bien_general.parking_type",
  transports_proximite: "bien_general.transports_proximite",
  classement_erp: "bien_general.classement_erp",

  // --- CHR specifics
  nombre_couverts: "bien_general.nombre_couverts",
  terrasse_desc: "bien_general.terrasse",
  cuisine_equipee: "bien_general.cuisine_equipee",
  type_cuisine: "composition.type_cuisine",
  chambre_froide: "composition.chambre_froide",

  // --- Office specifics
  configuration_bureaux: "composition.configuration_bureaux",
  salles_reunion: "bien_general.salles_reunion",
  parking_inclus: "bien_general.parking_inclus",

  // --- Retail specifics
  flux_pieton: "bien_general.flux_pieton",
  visibilite_rue: "bien_general.visibilite_rue",
  parking_proximite: "bien_general.parking_proximite",
  acces_livraison: "composition.acces_livraison",

  // --- Industrial/logistics specifics
  quais_chargement: "composition.quais_chargement",
  portes_sectionnelles: "composition.portes_sectionnelles",
  charge_sol: "composition.charge_sol",
  pont_roulant: "composition.pont_roulant",

  // --- Ateliers / Production-Réparation / Industriel (spécifiques)
  plain_pied: "composition.plain_pied",
  aire_manoeuvre: "composition.aire_manoeuvre",
  electricite_tri: "composition.electricite_tri",
  puissance_kw: "composition.puissance_kw",
  extraction: "composition.extraction_existe",
  icpe: "composition.icpe",

  // --- Licence specifics
  type_licence: "bien_general.type_licence",
  numero_licence: "bien_general.numero_licence",
  validite_licence: "bien_general.validite_licence",
  transferable: "bien_general.transferable",
  conditions_transfert: "bien_general.conditions_transfert",
  organisme_delivrance: "bien_general.organisme_delivrance",
  restrictions_licence: "bien_general.restrictions_licence",
  historique_exploitation: "bien_general.historique_exploitation",
  date_disponibilite: "bien_general.date_disponibilite",

  // --- Media
  photos: "medias.photos",
  documents: "medias.documents",
  plans: "medias.plans",
  video_url: "medias.video_url",
  visite_virtuelle_url: "medias.visite_virtuelle_url",

  // --- Public sharing
  is_public: "is_public",
  public_code: "public_code",
  public_password: "public_password",

  // --- Confidential (guarded elsewhere)
  informations_confidentielles: "informations_confidentielles",
  
  // --- Confidential > Financial data
  ca_n1: "informations_confidentielles.ca_n1",
  ca_n2: "informations_confidentielles.ca_n2",
  benefices_annuels: "informations_confidentielles.benefices_annuels",
  resultat_n1: "informations_confidentielles.benefices_annuels", // alias
  charges_annuelles: "informations_confidentielles.charges_annuelles",
  valeur_stock: "informations_confidentielles.valeur_stock",
  franchise: "informations_confidentielles.franchise",
  raison_vente: "informations_confidentielles.raison_vente",
  
  // --- Vente / Pricing (informations_vente)
  prix_cession: "informations_vente.prix_cession",
  net_vendeur_vente: "informations_vente.net_vendeur",
  type_prix_vente: "informations_vente.type_prix",
  honoraires_type_vente: "informations_vente.honoraires_type",
  honoraires_pourcentage_vente: "informations_vente.honoraires_pourcentage",
  honoraires_fixe_vente: "informations_vente.honoraires_fixe",
  honoraires_charge_vente: "informations_vente.honoraires_charge",
  negotiable_vente: "informations_vente.negociable",  // iter130 : le modèle attend `negociable`
  tva_applicable: "informations_vente.tva_applicable",
  taux_tva: "informations_vente.taux_tva",
  delai_liberation: "informations_vente.delai_liberation",
  date_disponibilite_vente: "informations_vente.date_disponibilite",
  conditions_particulieres_vente: "informations_vente.conditions_particulieres",
  
  // --- Location / Bail (informations_bail)
  charges_mensuelles_bail: "informations_bail.charges_mensuelles",  // iter130 : alias du même champ
  depot_garantie: "informations_bail.depot_garantie",
  honoraires_location_pourcentage: "informations_bail.honoraires_location_pourcentage",
  redevance_annuelle: "informations_bail.redevance_annuelle",
  conditions_particulieres_gerance: "informations_bail.conditions_particulieres_gerance",
  
  // --- Descriptions (always applicable)
  description_courte: "bien_general.description_courte",
  descriptif: "descriptif",
  
  // --- Services & Professions libérales (NEW)
  nb_pieces: "composition.nb_pieces",
  nb_bureaux: "composition.nb_bureaux",
  salle_attente: "composition.salle_attente",
  surface_salle_attente: "composition.surface_salle_attente",
  secretariat_accueil: "composition.secretariat_accueil",
  surface_secretariat: "composition.surface_secretariat",
  nb_sanitaires: "composition.nb_sanitaires",
  nb_salles_eau: "composition.nb_salles_eau",
  kitchenette: "composition.kitchenette",
  surface_kitchenette: "composition.surface_kitchenette",
  archives: "composition.archives",
  local_technique: "composition.local_technique",
  
  // --- Medical specifics
  nb_salles_consultation: "composition.nb_salles_consultation",
  nb_salles_soins: "composition.nb_salles_soins",
  nb_fauteuils_dentaires: "composition.nb_fauteuils_dentaires",
  salle_sterilisation: "composition.salle_sterilisation",
  radio_panoramique: "composition.radio_panoramique",
  salle_prelevement: "composition.salle_prelevement",
  stockage_refrigere: "composition.stockage_refrigere",
  vestiaires: "composition.vestiaires",
  normes_medicales: "composition.normes_medicales",
  pmr_obligatoire: "composition.pmr_obligatoire",
  
  // --- Juridique specifics
  nb_salles_reunion: "composition.nb_salles_reunion",
  salle_signature: "composition.salle_signature",
  archives_securisees: "composition.archives_securisees",
  coffre_fort: "composition.coffre_fort",
  insonorisation: "composition.insonorisation",
  
  // --- Commerce services specifics
  vitrine: "composition.vitrine",
  lineaire_vitrine: "composition.lineaire_vitrine",
  espace_accueil_client: "composition.espace_accueil_client",
  nb_postes_accueil: "composition.nb_postes_accueil",

  // --- NOUVEAUX CHAMPS (avril 2026)
  // Diagnostics énergétiques (vente_immo + location)
  dpe: "bien_general.dpe",
  ges: "bien_general.ges",
  dpe_date: "bien_general.dpe_date",
  ges_date: "bien_general.ges_date",
  // Performance & RH (cession + parts sociales + gérance)
  ebe: "informations_confidentielles.ebe",
  effectif_salaries: "informations_confidentielles.effectif_salaries",
  date_creation_etablissement: "informations_confidentielles.date_creation_etablissement",
  // Cession de parts sociales (SARL/SAS)
  capital_social: "informations_vente.capital_social",
  nombre_parts_sociales: "informations_vente.nombre_parts_sociales",
  pourcentage_capital_cede: "informations_vente.pourcentage_capital_cede",
  fonds_detenu_par_societe: "informations_vente.fonds_detenu_par_societe",
  murs_detenus_par_societe: "informations_vente.murs_detenus_par_societe",
  // Médias (visuel thématique pour licences/parts holding)
  visuel_thematique: "medias.visuel_thematique",
};

// ---------------------------
// Base sets (always relevant)
// ---------------------------
const BASE_ALWAYS = new Set([
  FIELDS.operation,
  FIELDS.objet,
  FIELDS.profil,
  FIELDS.sous_profil,
  FIELDS.cible,

  FIELDS.status,
  FIELDS.numero_mandat,
  FIELDS.date_mandat,
  FIELDS.type_mandat,
  FIELDS.negociateur,

  FIELDS.ville_generale,
  FIELDS.code_postal,
  FIELDS.secteur,
  FIELDS.zone_attractivite,
  FIELDS.emplacement,
  FIELDS.geoloc_lat,
  FIELDS.geoloc_lon,

  FIELDS.surface_totale,

  // Media are always allowed (not required)
  FIELDS.photos,
  FIELDS.documents,
  FIELDS.plans,
  FIELDS.video_url,
  FIELDS.visite_virtuelle_url,
  
  // Confidential info always allowed (propriétaire, etc.)
  FIELDS.informations_confidentielles,
  
  // Confidential financial data always allowed
  FIELDS.ca_n1,
  FIELDS.ca_n2,
  FIELDS.benefices_annuels,
  FIELDS.charges_annuelles,
  FIELDS.valeur_stock,
  FIELDS.franchise,
  FIELDS.raison_vente,
  
  // Descriptions always allowed
  FIELDS.description_courte,
  FIELDS.descriptif,
  
  // Deal tracking (closed_sale/closed_let) - always allowed
  "deal.status",
  "deal.closed_date",
  "deal.closed_amount",
  "deal.amount_period",
  "deal.notes",
  
  // Market status - always allowed
  "market.status",
]);

// ---------------------------
// Helpers
// ---------------------------
function normTaxonomy(taxonomy = {}) {
  return {
    operation: taxonomy.operation || null,
    objet: taxonomy.objet || null,
    profil: taxonomy.profil || null,
    sous_profil: taxonomy.sous_profil || null,
    cible: taxonomy.cible || null,
  };
}

function addMany(set, arr) {
  for (const x of arr) set.add(x);
}

function isOneOf(value, arr) {
  return value != null && arr.includes(value);
}

/**
 * Get value from nested object (safe accessor).
 * Used internally for reset and completion logic.
 */
function getByPathInternal(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

// ---------------------------
// Main: compute applicable fields by taxonomy
// ---------------------------
export function getApplicableFieldIds(taxonomy) {
  const t = normTaxonomy(taxonomy);
  const fields = new Set(BASE_ALWAYS);

  // ============================================
  // STEP 1: Add CORE fields from coreFieldsRules.json
  // ============================================
  
  // CRITICAL: Licence d'exploitation seule = actif incorporel sans local physique
  // → Pas de champs composition (surfaces, équipements, etc.)
  const isLicenceSeule = (t.objet === 'licence' || t.objet === 'licence_exploitation');
  
  // Add all common fields (always applicable)
  // BUT exclude composition.* fields if licence seule
  if (coreFieldsRules.common) {
    for (const field of coreFieldsRules.common) {
      // Skip composition fields for licence seule
      if (isLicenceSeule && field.section === 'composition') continue;

      // iter109 — Support appliesTo.profils / .objets / .sous_profils sur les champs communs.
      // Sémantique OR : si UNE des listes est spécifiée, le champ s'applique dès qu'AU MOINS une matche.
      const at = field.appliesTo || {};
      const hasAny = at.profils || at.objets || at.sous_profils;
      if (hasAny && !at.always) {
        const matches = (
          (at.profils && at.profils.includes(t.profil)) ||
          (at.objets && at.objets.includes(t.objet)) ||
          (at.sous_profils && at.sous_profils.includes(t.sous_profil))
        );
        if (!matches) continue;
      }

      const mappedPath = resolveFieldPath(field.id);
      fields.add(mappedPath);
    }
  }
  
  // Add profile-specific fields based on taxonomy.profil
  // Note: profil mapping between JSON and taxonomy
  const profilMapping = {
    'chr': 'chr',
    'retail': 'retail',
    'tertiaire': 'bureaux',  // tertiaire maps to bureaux profile
    'industriel': 'activite_logistique',
    'logistique': 'activite_logistique',
    'investissement': 'investissement_murs',
    'services_liberaux': 'services_liberaux',  // NEW: Services & Professions libérales
  };
  
  const jsonProfileKey = profilMapping[t.profil];
  
  // Only add profile-specific composition fields if NOT licence seule
  if (!isLicenceSeule && jsonProfileKey && coreFieldsRules.profiles[jsonProfileKey]) {
    const profileFields = coreFieldsRules.profiles[jsonProfileKey].fields;
    for (const field of profileFields) {
      // Skip computed fields (they don't count as applicable for completion)
      if (field.computed) continue;
      
      // Check if field applies to specific sous_profils
      if (field.appliesTo && field.appliesTo.sous_profils) {
        // Only add if current sous_profil is in the list
        if (!field.appliesTo.sous_profils.includes(t.sous_profil)) {
          continue;
        }
      }
      
      const mappedPath = resolveFieldPath(field.id);
      fields.add(mappedPath);
      
      // Note: 'when' conditions are NOT evaluated here (Option A decision).
      // Components will evaluate them at render time using isWhenSatisfied().
    }
  }
  
  // Special case: Licence objet should include licence-specific fields
  if (isLicenceSeule && coreFieldsRules.profiles.licence) {
    for (const field of coreFieldsRules.profiles.licence.fields) {
      if (field.computed) continue;
      const mappedPath = resolveFieldPath(field.id);
      fields.add(mappedPath);
    }
  }
  
  // ============================================
  // STEP 2: Keep existing operation-based logic + ENRICHMENT
  // ============================================
  
  // Location: loyer + charges + dépôt + honoraires
  if (t.operation === "location") {
    addMany(fields, [
      FIELDS.loyer_mensuel,
      FIELDS.loyer_annuel,
      FIELDS.charges_mensuelles,
      FIELDS.charges_mensuelles_bail,
      FIELDS.depot_garantie,
      FIELDS.honoraires_location_fixe,
      FIELDS.honoraires_location_pourcentage,
      FIELDS.titre_locatif,
      FIELDS.loyer_soumis_tva,
      FIELDS.date_debut_bail,
      FIELDS.date_echeance_bail,
      FIELDS.revision_loyer,
      FIELDS.impot_foncier,
      FIELDS.procedure,
      // lot BI §4 — la date de disponibilité manquait aux locations alors que le champ existe
      // déjà (même stockage `informations_vente.date_disponibilite` que la vente / cession).
      // Sans elle, aucun `management` ne partait à SeLoger pour un bien à louer.
      FIELDS.date_disponibilite_vente,
    ]);
  }
  
  // Location avec pas-de-porte: droit d'entrée + loyer + charges + dépôt + honoraires
  if (t.operation === "location_droit_entree") {
    addMany(fields, [
      FIELDS.droit_entree,
      FIELDS.loyer_mensuel,
      FIELDS.loyer_annuel,
      FIELDS.charges_mensuelles,
      FIELDS.charges_mensuelles_bail,
      FIELDS.depot_garantie,
      FIELDS.honoraires_location_fixe,
      FIELDS.honoraires_location_pourcentage,
      FIELDS.titre_locatif,
      FIELDS.loyer_soumis_tva,
      FIELDS.date_debut_bail,
      FIELDS.date_echeance_bail,
      FIELDS.revision_loyer,
      FIELDS.impot_foncier,
      FIELDS.procedure,
      // lot BI §4 — la date de disponibilité manquait aux locations alors que le champ existe
      // déjà (même stockage `informations_vente.date_disponibilite` que la vente / cession).
      // Sans elle, aucun `management` ne partait à SeLoger pour un bien à louer.
      FIELDS.date_disponibilite_vente,
    ]);
  }

  // Location-gérance: redevance + caution + conditions
  if (t.operation === "location_gerance") {
    addMany(fields, [
      FIELDS.redevance_mensuelle,
      FIELDS.redevance_annuelle,
      FIELDS.duree_gerance,
      FIELDS.caution_gerance,
      FIELDS.conditions_gerance,
      FIELDS.conditions_particulieres_gerance,
      // Often a bail context exists too
      FIELDS.titre_locatif,
      FIELDS.date_debut_bail,
      FIELDS.date_echeance_bail,
      FIELDS.revision_loyer,
      FIELDS.loyer_soumis_tva,
      FIELDS.impot_foncier,
      FIELDS.procedure,
    ]);
  }

  // Cession / Vente immobilière: prix + honoraires + TVA
  if (t.operation === "cession" || t.operation === "vente_immo") {
    addMany(fields, [
      FIELDS.prix_annonce,
      FIELDS.prix_cession,
      FIELDS.net_vendeur,
      FIELDS.net_vendeur_vente,
      FIELDS.prix_minimum_accepte,
      FIELDS.prix_vente,
      FIELDS.honoraires_type_vente,
      FIELDS.honoraires_pourcentage_vente,
      FIELDS.honoraires_fixe_vente,
      FIELDS.honoraires_charge_vente,
      FIELDS.negotiable_vente,
      FIELDS.tva_applicable,
      FIELDS.taux_tva,
      FIELDS.delai_liberation,
      FIELDS.date_disponibilite_vente,
      FIELDS.conditions_particulieres_vente,
    ]);
    // Cession ET vente_immo : un bail commercial existe presque toujours sur le bien
    // (cession = bail repris par l'acquéreur, vente_immo = bail en cours pour le bailleur)
    addMany(fields, [
      FIELDS.loyer_mensuel,
      FIELDS.loyer_annuel,
      FIELDS.charges_mensuelles,
      FIELDS.charges_mensuelles_bail,
      FIELDS.depot_garantie,
      FIELDS.titre_locatif,
      FIELDS.date_debut_bail,
      FIELDS.date_echeance_bail,
      FIELDS.revision_loyer,
      FIELDS.loyer_soumis_tva,
      FIELDS.impot_foncier,
      FIELDS.procedure,
    ]);
  }

  // --- Object-based: licence
  if (t.objet === "licence") {
    addMany(fields, [
      FIELDS.type_licence,
      FIELDS.numero_licence,
      FIELDS.validite_licence,
      FIELDS.transferable,
      FIELDS.conditions_transfert,
      FIELDS.organisme_delivrance,
      FIELDS.restrictions_licence,
      FIELDS.historique_exploitation,
      FIELDS.date_disponibilite,
      // Visuel thématique pour licence
      FIELDS.visuel_thematique,
    ]);
  }

  // --- DPE / GES : applicable à TOUT bien avec un local physique
  //     (cession de fonds, vente de murs, locations, location-gérance, droit au bail…)
  //     SAUF les actifs incorporels : licence d'exploitation et parts sociales
  //     (pas de local physique → pas de DPE).
  const isIncorporel = t.objet === "licence_exploitation" || t.objet === "licence" || t.objet === "parts_sociales" || t.operation === "licence" || t.operation === "parts_sociales";
  if (!isIncorporel) {
    addMany(fields, [
      FIELDS.dpe,
      FIELDS.ges,
      FIELDS.dpe_date,
      FIELDS.ges_date,
    ]);
  }

  // --- EBE / Effectif / Date création (cession fonds, parts sociales, location-gérance, vente_immo)
  if (
    t.operation === "cession" ||
    t.operation === "location_gerance" ||
    t.operation === "vente_immo" ||
    t.objet === "parts_sociales"
  ) {
    addMany(fields, [
      FIELDS.ebe,
      FIELDS.effectif_salaries,
      FIELDS.date_creation_etablissement,
    ]);
  }

  // --- Prix pivot (net vendeur) + prix FAI calculé — actifs incorporels
  //     (licence de débit de boissons vendue seule, parts sociales) : ces opérations
  //     n'entrent pas dans la branche cession/vente_immo mais ont bien un prix.
  if (t.operation === "parts_sociales" || t.operation === "licence"
      || t.objet === "parts_sociales" || t.objet === "licence" || t.objet === "licence_exploitation") {
    addMany(fields, [
      FIELDS.net_vendeur_vente,
      FIELDS.prix_cession,
      FIELDS.honoraires_charge_vente,
    ]);
  }

  // --- Champs spécifiques aux parts sociales (capital, % cédé, fonds/murs détenus)
  if (t.objet === "parts_sociales") {    addMany(fields, [
      FIELDS.capital_social,
      FIELDS.nombre_parts_sociales,
      FIELDS.pourcentage_capital_cede,
      FIELDS.fonds_detenu_par_societe,
      FIELDS.murs_detenus_par_societe,
      // Visuel thématique aussi pour parts holding (pas de local)
      FIELDS.visuel_thematique,
    ]);
  }

  // --- Bail also relevant for fonds/DAB and occupied walls
  if (isOneOf(t.objet, ["fonds_de_commerce", "droit_au_bail"])) {
    addMany(fields, [
      FIELDS.titre_locatif,
      FIELDS.date_debut_bail,
      FIELDS.loyer_mensuel,
      FIELDS.loyer_annuel,
      FIELDS.charges_mensuelles,
      FIELDS.loyer_soumis_tva,
    ]);
  }

  if (isOneOf(t.objet, ["murs_commerciaux", "immeuble"])) {
    // Bail fields can still be relevant depending on occupancy; keep them available
    addMany(fields, [
      FIELDS.titre_locatif,
      FIELDS.date_debut_bail,
      FIELDS.loyer_mensuel,
      FIELDS.loyer_annuel,
      FIELDS.charges_mensuelles,
      FIELDS.impot_foncier,
    ]);
  }

  // --- Profile-based dynamic blocks
  if (t.profil === "chr") {
    addMany(fields, [
      FIELDS.nombre_couverts,
      FIELDS.nombre_couverts_total,
      FIELDS.places_interieur,
      FIELDS.places_terrasse,
      FIELDS.terrasse_desc,
      FIELDS.terrasse_m2,
      FIELDS.cuisine_equipee,
      FIELDS.surface_cuisine,
      FIELDS.type_cuisine,
      FIELDS.chambre_froide,
      // iter109 — 'surface_vente' retiré du CHR (audit utilisateur) : c'est un champ retail.
      FIELDS.reserve,
      FIELDS.lineaire_facade,
    ]);
  }

  if (t.profil === "tertiaire" || t.objet === "bureaux") {
    addMany(fields, [
      FIELDS.configuration_bureaux,
      FIELDS.postes_travail,
      FIELDS.salles_reunion,
      FIELDS.salles_reunion_nombre,
      FIELDS.surface_bureaux,
      FIELDS.surface_salles_reunion,
      FIELDS.parking_inclus,
      FIELDS.parking_places,
      FIELDS.fibre_optique,
      FIELDS.climatisation,
      FIELDS.classement_erp,
    ]);
  }

  if (t.profil === "retail" || t.objet === "local_commercial") {
    addMany(fields, [
      FIELDS.flux_pieton,
      FIELDS.visibilite_rue,
      FIELDS.parking_proximite,
      FIELDS.acces_livraison,
      FIELDS.surface_vente,
      FIELDS.reserve,
      FIELDS.lineaire_facade,
    ]);
  }

  if (t.profil === "industriel" || t.profil === "logistique" || isOneOf(t.objet, ["local_activite", "entrepot_logistique"])) {
    addMany(fields, [
      FIELDS.surface_stockage,
      FIELDS.surface_production,
      FIELDS.hauteur_plafond,
      FIELDS.hauteur_libre,
      FIELDS.quais_chargement,
      FIELDS.portes_sectionnelles,
      FIELDS.charge_sol,
      FIELDS.pont_roulant,
      FIELDS.acces_livraison,
      FIELDS.parking_places,
    ]);
  }

  // --- Production / Réparation / Artisanat (nouveau profil MVP-3)
  // Sous-ensemble ciblé des champs "Activité/Logistique" — pas de quais/portes/pont/sprinkler/frigo/T°dirigée
  if (t.profil === "production_reparation") {
    addMany(fields, [
      FIELDS.electricite_tri,     // Électricité triphasée
      FIELDS.puissance_kw,         // Puissance (kW)
      FIELDS.extraction,           // Extraction
      FIELDS.hauteur_plafond,      // Hauteur sous plafond
      FIELDS.hauteur_libre,        // Hauteur libre
      FIELDS.charge_sol,           // Charge au sol (kg/m²)
      FIELDS.plain_pied,           // Plain-pied
      FIELDS.acces_livraison,      // Accès
      FIELDS.aire_manoeuvre,       // Aire de manœuvre
      FIELDS.icpe,                 // ICPE
      FIELDS.parking_places,
    ]);
  }

  // --- Common technicals generally useful everywhere
  addMany(fields, [
    FIELDS.accessibilite_pmr,
    FIELDS.parking_type,
    FIELDS.transports_proximite,
    FIELDS.fibre_optique,
    FIELDS.climatisation,
  ]);

  return Array.from(fields);
}

// ---------------------------
// Reset logic: remove non-applicable fields safely
// ---------------------------

function setByPath(obj, path, value) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (cur[p] == null || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

function resetValueLike(existing) {
  // CRITICAL: Always return null to ensure non-applicable fields never contain exploitable values
  // 0, "", false, [] are all exploitable values that could be mistaken for real data
  return null;
}

/**
 * Migration safety: never auto-clear these paths on a taxonomy change.
 * - composition.* : surfaces/capacités/équipements restent valides quelle que soit la transaction.
 * - is_public / public_* : la visibilité publique ne dépend pas de la taxonomie.
 * L'utilisateur reste maître de la suppression manuelle (jamais d'effacement silencieux).
 */
function isProtectedFromReset(path) {
  if (!path) return false;
  if (path.startsWith("composition.")) return true;
  if (path === "is_public" || path === "public_code" || path === "public_password") return true;
  return false;
}

/**
 * Resets every field that is not applicable for the current taxonomy.
 * Keeps taxonomy fields + base always fields.
 * Also resets CORE fields from coreFieldsRules.json that are not applicable.
 * Returns a NEW object (immutable).
 */
export function resetNonApplicableFields(formData, taxonomy) {
  const applicable = new Set(getApplicableFieldIds(taxonomy));
  const next = structuredClone(formData || {});

  // Reset known fields listed in FIELDS (existing system)
  for (const key of Object.keys(FIELDS)) {
    const path = FIELDS[key];
    if (!path) continue;

    // never reset taxonomy keys
    if (
      path === FIELDS.operation ||
      path === FIELDS.objet ||
      path === FIELDS.profil ||
      path === FIELDS.sous_profil ||
      path === FIELDS.cible
    ) {
      continue;
    }

    if (!applicable.has(path)) {
      if (isProtectedFromReset(path)) continue;
      const existing = getByPathInternal(next, path);
      if (existing !== undefined) {
        setByPath(next, path, resetValueLike(existing));
      }
    }
  }
  
  // Reset CORE fields from coreFieldsRules.json
  // Process common fields
  if (coreFieldsRules.common) {
    for (const field of coreFieldsRules.common) {
      const mappedPath = resolveFieldPath(field.id);
      if (!applicable.has(mappedPath)) {
        if (isProtectedFromReset(mappedPath)) continue;
        const existing = getByPathInternal(next, mappedPath);
        if (existing !== undefined) {
          setByPath(next, mappedPath, resetValueLike(existing));
        }
      }
    }
  }
  
  // Process all profile-specific fields
  if (coreFieldsRules.profiles) {
    for (const profileKey of Object.keys(coreFieldsRules.profiles)) {
      const profileFields = coreFieldsRules.profiles[profileKey].fields;
      for (const field of profileFields) {
        const mappedPath = resolveFieldPath(field.id);
        if (!applicable.has(mappedPath)) {
          if (isProtectedFromReset(mappedPath)) continue;
          const existing = getByPathInternal(next, mappedPath);
          if (existing !== undefined) {
            setByPath(next, mappedPath, resetValueLike(existing));
          }
        }
      }
    }
  }

  return next;
}

// ---------------------------
// Completion percent (optional)
// ---------------------------
// Simple and deterministic scoring. Adjust weights as you like.
const COMPLETION_BUCKETS = [
  {
    name: "taxonomy",
    weight: 20,
    fields: [FIELDS.operation, FIELDS.objet, FIELDS.profil, FIELDS.sous_profil, FIELDS.cible],
  },
  {
    name: "essentials",
    weight: 35,
    fields: [
      FIELDS.ville_generale,
      FIELDS.code_postal,
      FIELDS.secteur,
      FIELDS.surface_totale,
      FIELDS.status,
    ],
  },
  {
    name: "mandate",
    weight: 15,
    fields: [FIELDS.negociateur, FIELDS.date_mandat, FIELDS.numero_mandat],
    // numero_mandat only required if status=active; handled below
  },
  {
    name: "financial_core",
    weight: 20,
    // price vs rent depends on operation; handled below
    fields: [FIELDS.net_vendeur_vente, FIELDS.loyer_mensuel],
  },
  {
    name: "media",
    weight: 10,
    fields: [FIELDS.photos, FIELDS.documents],
  },
];

function isFilled(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return false;
}

/**
 * Build a set of all computed field paths from coreFieldsRules.json.
 * Computed fields should NOT be counted in completion percentage.
 */
function getComputedFieldPaths() {
  const computed = new Set();
  
  if (coreFieldsRules.profiles) {
    for (const profileKey of Object.keys(coreFieldsRules.profiles)) {
      const profileFields = coreFieldsRules.profiles[profileKey].fields;
      for (const field of profileFields) {
        if (field.computed) {
          const mappedPath = resolveFieldPath(field.id);
          computed.add(mappedPath);
        }
      }
    }
  }
  
  return computed;
}

export function computeCompletionPercent(formData, taxonomy) {
  const t = normTaxonomy(taxonomy);
  const applicable = new Set(getApplicableFieldIds(taxonomy));
  const computedFields = getComputedFieldPaths();
  const data = formData || {};

  let total = 0;
  let earned = 0;

  for (const bucket of COMPLETION_BUCKETS) {
    const bucketWeight = bucket.weight;
    total += bucketWeight;

    // evaluate bucket completeness as fraction of its relevant fields
    let fieldsToCheck = bucket.fields.slice();

    // mandate special rule: numero_mandat only required if status is active
    if (bucket.name === "mandate") {
      const status = getByPathInternal(data, FIELDS.status);
      if (status !== "active") {
        fieldsToCheck = fieldsToCheck.filter((f) => f !== FIELDS.numero_mandat);
      }
    }

    // financial core: choose price OR rent depending on operation
    if (bucket.name === "financial_core") {
      if (t.operation === "location" || t.operation === "location_gerance") {
        fieldsToCheck = [FIELDS.loyer_mensuel];
      } else {
        fieldsToCheck = [FIELDS.net_vendeur_vente];
      }
    }

    // only count fields that are applicable AND not computed
    fieldsToCheck = fieldsToCheck.filter((f) => applicable.has(f) && !computedFields.has(f));

    if (fieldsToCheck.length === 0) {
      earned += bucketWeight; // nothing to fill => considered complete
      continue;
    }

    const filledCount = fieldsToCheck.reduce((acc, path) => acc + (isFilled(getByPathInternal(data, path)) ? 1 : 0), 0);
    const ratio = filledCount / fieldsToCheck.length;
    earned += bucketWeight * ratio;
  }

  const percent = total === 0 ? 0 : Math.round((earned / total) * 100);
  return Math.max(0, Math.min(100, percent));
}
